/**
 * App Layout Component
 * Main layout wrapper with Bottom Navigation and Session Drawer
 * 
 * Features:
 * - Wraps page content with proper padding
 * - Includes BottomNavigation component
 * - Manages SessionDrawer state
 * - Prevents content from hiding behind nav
 * - Hides navigation on Quran Reader page (/read)
 */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import BottomNavigation from './BottomNavigation';
import DashboardHeader from './DashboardHeader';
import SessionDrawer from '@/components/sessions/SessionDrawer';

interface AppLayoutProps {
  children: React.ReactNode;
}

function getMainClassName(isReaderPage: boolean, showBottomNav: boolean): string {
  if (isReaderPage) return '';
  return showBottomNav ? 'pt-14 pb-24' : 'pt-14 pb-6';
}

function getMainStyle(isReaderPage: boolean, showBottomNav: boolean): { minHeight: string } | undefined {
  if (isReaderPage) return undefined;
  return { minHeight: showBottomNav ? 'calc(100vh - 6rem)' : 'calc(100vh - 3.5rem)' };
}

export function AppLayout({ children }: Readonly<AppLayoutProps>) {
  const location = useLocation();
  const [isSessionDrawerOpen, setIsSessionDrawerOpen] = useState(false);

  // Check if we're on the Quran Reader page or Feedback page (no bottom nav)
  const isReaderPage = location.pathname === '/read';
  const isFeedbackPage = location.pathname === '/feedback';
  const showBottomNav = !isReaderPage && !isFeedbackPage;

  const handleSessionClick = () => {
    setIsSessionDrawerOpen(true);
  };

  const handleSessionClose = () => {
    setIsSessionDrawerOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hide header on reader page */}
      {!isReaderPage && <DashboardHeader />}
      
      {/* Main Content */}
      <main
        className={getMainClassName(isReaderPage, showBottomNav)}
        style={getMainStyle(isReaderPage, showBottomNav)}
      >
        {children}
      </main>

      {/* Hide Bottom Navigation on reader and feedback pages */}
      {showBottomNav && (
        <BottomNavigation onSessionClick={handleSessionClick} />
      )}

      {/* Session Recording Drawer */}
      <SessionDrawer 
        open={isSessionDrawerOpen}
        onClose={handleSessionClose}
      />
    </div>
  );
}

export default AppLayout;
